import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { errorMessage, useTRPC } from "../../lib/api";
import type { MyWorkspace } from "../../lib/me";
import { ErrorText, Section } from "../../ui";

export interface WorkspaceCounts {
  workspaceId: string;
  unreadThreads: number;
  myTasksDueSoon: number;
}

/** The counts table itself, separate from its query so it can be tested with plain data. */
export function SummaryView({ workspaces, counts }: { workspaces: readonly MyWorkspace[]; counts: readonly WorkspaceCounts[] }) {
  const name = new Map(workspaces.map((w) => [w.id, w.name]));
  const unread = counts.reduce((n, c) => n + c.unreadThreads, 0);
  const due = counts.reduce((n, c) => n + c.myTasksDueSoon, 0);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text">
        <strong>{unread}</strong> unread {unread === 1 ? "thread" : "threads"} · <strong>{due}</strong> of my tasks due
        within a week
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-muted">
            <tr>
              <th scope="col" className="py-2 pr-4 font-medium">
                Workspace
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-medium">
                Unread
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                Due soon
              </th>
            </tr>
          </thead>
          <tbody>
            {counts.map((c) => {
              const label = name.get(c.workspaceId);
              return (
                <tr key={c.workspaceId} className="border-t border-border">
                  <th scope="row" className="py-2 pr-4 font-medium">
                    <Link
                      to="/w/$workspaceId/dashboard"
                      params={{ workspaceId: c.workspaceId }}
                      className="text-accent underline-offset-2 hover:underline"
                    >
                      {label === undefined ? c.workspaceId : label}
                    </Link>
                  </th>
                  <td className="py-2 pr-4 text-right tabular-nums">{c.unreadThreads}</td>
                  <td className="py-2 text-right tabular-nums">{c.myTasksDueSoon}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Header counts across every workspace the person belongs to (`dashboard.summary`). */
export function SummaryTable({ workspaces }: { workspaces: readonly MyWorkspace[] }) {
  const trpc = useTRPC();
  const workspaceIds = workspaces.slice(0, 20).map((w) => w.id);
  const summary = useQuery({ ...trpc.dashboard.summary.queryOptions({ workspaceIds }), enabled: workspaceIds.length > 0 });
  return (
    <Section title="At a glance">
      {summary.isPending ? <p className="text-sm text-muted">Loading counts…</p> : null}
      {summary.isError ? <ErrorText>{errorMessage(summary.error)}</ErrorText> : null}
      {summary.isSuccess ? <SummaryView workspaces={workspaces} counts={summary.data} /> : null}
    </Section>
  );
}
